// /welcome and /welcome/:role — a short walk through Fable+ for customers, creators and partners.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, BadgeCheck, Banknote, BarChart3, Calendar, Camera, Check, CreditCard, LayoutGrid, Megaphone,
  MessageCircle, Plane, Play, Radio, ShieldCheck, Sparkles, Store, User, Users, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const ICONS = {
  grid: LayoutGrid, calendar: Calendar, play: Play, chat: MessageCircle, plane: Plane, wallet: Wallet, sparkles: Sparkles,
  person: User, camera: Camera, chart: BarChart3, live: Radio, people: Users, cash: Banknote, card: CreditCard,
  shield: ShieldCheck, storefront: Store, megaphone: Megaphone, bank: Banknote,
};
const ROLE_LABEL = { customer: "Using Fable+", creator: "Creating on Stage", provider: "Running a business" };

function Chooser() {
  const [list, setList] = useState(null);
  const { user } = useAuth();
  useEffect(() => { document.title = "How Fable+ works"; api("/guides", { auth: false }).then((d) => setList(d.guides)).catch(() => setList([])); }, []);
  const done = new Set(user?.guides_done || []);
  return (
    <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6" data-testid="guides">
      <h1 className="text-4xl font-bold tracking-tight">How Fable+ works</h1>
      <p className="mt-3 max-w-xl text-muted-foreground">Pick the walkthrough that fits you. Each one takes about a minute, and you can come back any time.</p>
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {(list || []).map((g) => (
          <Link key={g.role} to={"/welcome/" + g.role} className="group rounded-2xl border bg-card p-5 transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={"guide-" + g.role}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-muted-foreground">{ROLE_LABEL[g.role] || g.role}</span>
              {done.has(g.role) && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600"><BadgeCheck className="h-4 w-4" />Done</span>}
            </div>
            <h2 className="mt-2 text-lg font-bold">{g.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{g.intro}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">{g.steps} steps <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" /></span>
          </Link>
        ))}
        {!list && [0, 1, 2].map((i) => <div key={i} className="h-44 animate-pulse rounded-2xl bg-muted" />)}
      </div>
    </main>
  );
}

function Walkthrough({ role }) {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [g, setG] = useState(null);
  const [i, setI] = useState(0);
  useEffect(() => {
    api("/guides/" + role, { auth: false }).then((d) => { setG(d); document.title = d.title + " · Fable+"; }).catch(() => navigate("/welcome", { replace: true }));
  }, [role, navigate]);
  const n = g?.steps?.length || 0;
  const finish = useCallback(async () => {
    if (user) { try { await api("/guides/" + role + "/done", { method: "POST" }); await refresh?.(); } catch { /* not essential */ } }
    navigate("/welcome");
  }, [user, role, refresh, navigate]);
  useEffect(() => {
    const k = (e) => {
      if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
      if (e.key === "ArrowRight") setI((x) => Math.min(x + 1, n - 1));
      if (e.key === "ArrowLeft") setI((x) => Math.max(x - 1, 0));
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [n]);
  if (!g) return <main className="mx-auto max-w-5xl px-4 py-12"><div className="h-80 animate-pulse rounded-3xl bg-muted" /></main>;
  const s = g.steps[i];
  const Icon = ICONS[s.icon] || Sparkles;
  const last = i === n - 1;
  return (
    <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="guide-walkthrough">
      <Link to="/welcome" className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />All guides</Link>
      <h1 className="mt-3 text-3xl font-bold tracking-tight">{g.title}</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">{g.intro}</p>
      <div className="mt-8 grid gap-6 md:grid-cols-[240px_minmax(0,1fr)]">
        <ol className="flex gap-2 overflow-x-auto md:flex-col" aria-label="Steps">
          {g.steps.map((x, j) => (
            <li key={x.key} className="shrink-0">
              <button type="button" onClick={() => setI(j)} aria-current={j === i ? "step" : undefined}
                className={"flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold transition " + (j === i ? "bg-primary text-primary-foreground" : "hover:bg-muted")}>
                <span className={"flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs " + (j < i ? "bg-emerald-500 text-white" : j === i ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground")}>
                  {j < i ? <Check className="h-3.5 w-3.5" /> : j + 1}
                </span>
                <span className="whitespace-nowrap md:whitespace-normal">{x.title}</span>
              </button>
            </li>
          ))}
        </ol>
        <section className="rounded-3xl border bg-card p-6 sm:p-10" aria-live="polite">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ background: "var(--user-accent, #E0007A)" }}><Icon className="h-7 w-7" aria-hidden="true" /></div>
          <p className="mt-6 text-sm font-semibold text-muted-foreground">Step {i + 1} of {n}</p>
          <h2 className="mt-1 text-2xl font-bold">{s.title}</h2>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">{s.body}</p>
          {s.web && <Link to={s.web} className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">Open it now <ArrowRight className="h-4 w-4" /></Link>}
          <div className="mt-10 flex items-center justify-between gap-3">
            <Button type="button" variant="outline" onClick={() => setI((x) => Math.max(x - 1, 0))} disabled={i === 0}>Back</Button>
            <div className="flex gap-1.5" aria-hidden="true">{g.steps.map((x, j) => <span key={x.key} className={"h-1.5 rounded-full transition-all " + (j === i ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30")} />)}</div>
            {last ? <Button type="button" onClick={finish} data-testid="guide-finish">Finish</Button> : <Button type="button" onClick={() => setI((x) => x + 1)} data-testid="guide-next">Next</Button>}
          </div>
        </section>
      </div>
    </main>
  );
}

export default function Guide() {
  const { role } = useParams();
  return role ? <Walkthrough role={role} /> : <Chooser />;
}
